import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';

const SECTION_LABELS = { variable: 'Переменные', fixed: 'Постоянные', commercial: 'Коммерческие', administrative: 'Административные', tax: 'Налоги', depreciation: 'Амортизация', interest: 'Проценты по кредитам' };
const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;
const formatHours = (h) => {
  if (h == null) return '—';
  if (h >= 24) return `${Math.floor(h / 24)} дн ${Math.round(h % 24)} ч`;
  if (h >= 1) return `${h.toFixed(1)} ч`;
  return `${Math.round(h * 60)} мин`;
};
const formatDuration = (sec) => {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m} мин ${s} сек`;
};

function Bar({ label, value, max }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span>{label}</span><span className="text-muted">{money(value)}</span>
      </div>
      <div style={{ background: 'var(--bg-input)', borderRadius: 4, height: 8 }}>
        <div style={{ width: `${pct}%`, background: 'var(--primary)', height: 8, borderRadius: 4 }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amoData, setAmoData] = useState(null);
  const [amoRefreshing, setAmoRefreshing] = useState(false);

  const loadAmo = () => api.get('/finance/reports/amo-funnel').then(r => setAmoData(r.data)).catch(() => {});

  useEffect(() => {
    setLoading(true);
    api.get('/finance/reports/dashboard', { params: { months: 12 } }).then(r => setData(r.data)).finally(() => setLoading(false));
    loadAmo();
  }, []);

  const refreshAmo = async () => {
    setAmoRefreshing(true);
    try {
      await api.post('/finance/reports/amo-funnel/refresh');
      await loadAmo();
    } catch (err) {
      window.alert(err.response?.data?.error || 'Ошибка обновления');
    } finally { setAmoRefreshing(false); }
  };

  if (loading) return <div className="loading-text">Загрузка...</div>;
  if (!data) return <div className="empty-state">Нет данных</div>;

  const lastPnl = data.pnlByMonth[data.pnlByMonth.length - 1];
  const maxSection = Math.max(1, ...data.bySection.map(s => Number(s.total)));
  const maxCostType = Math.max(1, ...data.byCostType.map(s => Number(s.total)));

  return (
    <div>
      {lastPnl && (
        <div className="stats-row">
          <div className="stat-card"><div className="stat-value">{money(lastPnl.revenue)}</div><div className="stat-label">Выручка, текущий месяц</div></div>
          <div className="stat-card"><div className="stat-value">{money(lastPnl.marginProfit)}</div><div className="stat-label">Маржинальная прибыль</div></div>
          <div className="stat-card"><div className="stat-value">{lastPnl.marginRentability.toFixed(1)}%</div><div className="stat-label">Маржинальная рентабельность</div></div>
          <div className="stat-card"><div className="stat-value">{lastPnl.netRentability.toFixed(1)}%</div><div className="stat-label">Итоговая рентабельность</div></div>
        </div>
      )}

      <h3 className="mb-2">Выручка и расходы по месяцам</h3>
      <div className="table-wrap mb-2">
        <table>
          <thead><tr><th>Месяц</th><th>Выручка</th><th>Расходы</th></tr></thead>
          <tbody>
            {data.pnlByMonth.map(m => (
              <tr key={m.month}>
                <td>{m.month}</td>
                <td>{money(m.revenue)}</td>
                <td>{money(m.variable + m.fixed + m.commercial + m.administrative + m.tax + m.depreciation + m.interest)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-row">
        <div>
          <h3 className="mb-2">Расходы по разделам бюджета</h3>
          {data.bySection.length === 0 ? <div className="empty-state">Нет данных за период</div> :
            data.bySection.map(s => <Bar key={s.section} label={s.section} value={Number(s.total)} max={maxSection} />)}
        </div>
        <div>
          <h3 className="mb-2">Расходы по типу издержек</h3>
          {data.byCostType.length === 0 ? <div className="empty-state">Нет данных за период</div> :
            data.byCostType.map(s => <Bar key={s.cost_type || '—'} label={SECTION_LABELS[s.cost_type] || '—'} value={Number(s.total)} max={maxCostType} />)}
        </div>
      </div>

      <h3 className="mb-2">Остатки по счетам</h3>
      <div className="stats-row">
        {data.accountBalances.length === 0 ? <div className="empty-state">Счета не подключены</div> :
          data.accountBalances.map(a => (
            <div className="stat-card" key={a.id}><div className="stat-value">{money(a.balance)}</div><div className="stat-label">{a.name}</div></div>
          ))}
      </div>

      {data.dealStats && (
        <>
          <h3 className="mb-2">Проекты</h3>
          <div className="stats-row">
            <div className="stat-card"><div className="stat-value">{data.dealStats.total}</div><div className="stat-label">Всего проектов</div></div>
            <div className="stat-card"><div className="stat-value">{data.dealStats.inProgress}</div><div className="stat-label">В работе</div></div>
            <div className="stat-card"><div className="stat-value">{data.dealStats.closed}</div><div className="stat-label">Закрыто (выполнено и оплачено)</div></div>
            <div className="stat-card" style={{ borderColor: data.lowMarginDeals.length ? 'var(--danger)' : undefined }}>
              <div className="stat-value" style={{ color: data.lowMarginDeals.length ? 'var(--danger)' : undefined }}>{data.lowMarginDeals.length}</div>
              <div className="stat-label">Ниже маржи {data.dealStats.marginThreshold}%</div>
            </div>
          </div>

          {data.lowMarginDeals.length > 0 && (
            <div className="table-wrap mb-2">
              <table>
                <thead><tr><th>Проект</th><th>Оплачено</th><th>Расходы</th><th>Маржа</th></tr></thead>
                <tbody>
                  {data.lowMarginDeals.map(d => (
                    <tr key={d.id}>
                      <td><Link to={`/projects/deal/${d.id}`}>{d.name}</Link></td>
                      <td>{money(d.paid)}</td>
                      <td>{money(d.actualExpenses)}</td>
                      <td style={{ color: 'var(--danger)' }}>{d.marginPercent.toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {data.paymentQueue && (
        <>
          <h3 className="mb-2">Заявки на оплату и расчёты с контрагентами</h3>
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-value">{money(data.paymentQueue.total)}</div>
              <div className="stat-label">В очереди на оплату ({data.paymentQueue.count} шт.)</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{money(data.payables.total)}</div>
              <div className="stat-label">Кредиторская задолженность ({data.payables.count} шт.)</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{money(data.receivables.total)}</div>
              <div className="stat-label">Дебиторская задолженность ({data.receivables.count} контрагентов)</div>
            </div>
          </div>
        </>
      )}

      {data.designerWorkload && data.designerWorkload.length > 0 && (
        <>
          <h3 className="mb-2">Загрузка дизайнеров</h3>
          <div className="table-wrap mb-2">
            <table>
              <thead><tr><th>Дизайнер</th><th>В работе</th><th>Сдано в этом месяце</th></tr></thead>
              <tbody>
                {data.designerWorkload.map(d => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>{d.in_progress}</td>
                    <td>{d.done_this_month}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {amoData && (
        <>
          <div className="toolbar" style={{ marginTop: 16 }}>
            <h3 style={{ margin: 0 }}>Воронка продаж (AmoCRM)</h3>
            <div className="toolbar-spacer" />
            <span className="text-muted" style={{ fontSize: 12 }}>
              Обновлено: {new Date(amoData.updated_at).toLocaleString('ru-RU')}
            </span>
            {user.role === 'admin' && (
              <button className="btn-secondary btn-sm" onClick={refreshAmo} disabled={amoRefreshing}>
                {amoRefreshing ? 'Обновление...' : 'Обновить сейчас'}
              </button>
            )}
          </div>

          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-value">{amoData.data.totalInProgress}</div>
              <div className="stat-label">Сейчас в работе</div>
            </div>
            <div className="stat-card" style={{ borderColor: amoData.data.awaitingFirstCall.count ? 'var(--danger)' : undefined }}>
              <div className="stat-value" style={{ color: amoData.data.awaitingFirstCall.count ? 'var(--danger)' : undefined }}>{amoData.data.awaitingFirstCall.count}</div>
              <div className="stat-label">Висят без единого звонка</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatHours(amoData.data.timeToFirstCall.medianHours)}</div>
              <div className="stat-label">Медиана до первого звонка ({amoData.data.timeToFirstCall.count} шт.)</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatHours(amoData.data.timeToFirstCall.avgHours)}</div>
              <div className="stat-label">Среднее до первого звонка</div>
            </div>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginTop: -8 }}>
            Медиана обычно намного меньше среднего — многие лиды создаются сразу из входящего звонка (значит «звонок» и «создание» происходят одновременно), а среднее вытягивают вверх лиды из других каналов (сайт, Avito), которым перезванивают не сразу.
          </p>

          {amoData.data.awaitingFirstCall.count > 0 && (
            <>
              <h4 className="mb-2">Заявки без единого звонка (топ по времени ожидания)</h4>
              <div className="table-wrap mb-2">
                <table>
                  <thead><tr><th>Сделка</th><th>Воронка</th><th>Стадия</th><th>Висит</th></tr></thead>
                  <tbody>
                    {amoData.data.awaitingFirstCall.items.map(item => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.pipeline}</td>
                        <td>{item.stage}</td>
                        <td style={{ color: item.hoursWaiting > 48 ? 'var(--danger)' : undefined }}>{formatHours(item.hoursWaiting)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {amoData.data.callsByManager.length > 0 && (
            <>
              <h4 className="mb-2">Звонки по менеджерам</h4>
              <div className="table-wrap mb-2">
                <table>
                  <thead><tr><th>Менеджер</th><th>Звонков всего</th><th>Дозвонились</th><th>Суммарно на разговорах</th><th>Средняя длительность</th></tr></thead>
                  <tbody>
                    {amoData.data.callsByManager.map(m => (
                      <tr key={m.managerId}>
                        <td>{m.managerName}</td>
                        <td>{m.totalCalls}</td>
                        <td>{m.answeredCalls}</td>
                        <td>{formatDuration(m.totalDurationSec)}</td>
                        <td>{m.answeredCalls ? formatDuration(m.avgDurationSec) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {amoData.data.recentCalls && amoData.data.recentCalls.length > 0 && (
            <>
              <h4 className="mb-2">Последние звонки</h4>
              <div className="table-wrap mb-2">
                <table>
                  <thead><tr><th>Когда</th><th>Сделка</th><th>Менеджер</th><th>Направление</th><th>Длительность</th><th>Запись</th></tr></thead>
                  <tbody>
                    {amoData.data.recentCalls.map((c, i) => (
                      <tr key={i}>
                        <td>{new Date(c.createdAt * 1000).toLocaleString('ru-RU')}</td>
                        <td>{c.leadName}</td>
                        <td>{c.managerName}</td>
                        <td>{c.direction === 'in' ? 'Входящий' : 'Исходящий'}</td>
                        <td>{c.durationSec > 0 ? formatDuration(c.durationSec) : 'не дозвонились'}</td>
                        <td>{c.recordingUrl ? <a href={c.recordingUrl} target="_blank" rel="noreferrer">Прослушать</a> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h4 className="mb-2">Новые сделки по месяцам</h4>
          <div className="table-wrap mb-2">
            <table>
              <thead><tr><th>Месяц</th><th>Кол-во</th><th>Сумма</th></tr></thead>
              <tbody>
                {amoData.data.newLeadsByMonth.map(m => (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td>{m.count}</td>
                    <td>{money(m.sum)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-row">
            <div>
              <h4 className="mb-2">Текущий срез по стадиям (открытые сделки)</h4>
              {amoData.data.byStage.length === 0 ? <div className="empty-state">Нет открытых сделок</div> : (
                <div className="table-wrap mb-2">
                  <table>
                    <thead><tr><th>Воронка</th><th>Стадия</th><th>Кол-во</th><th>Сумма</th></tr></thead>
                    <tbody>
                      {amoData.data.byStage.map((s, i) => (
                        <tr key={i}>
                          <td>{s.pipeline}</td>
                          <td>{s.stage}</td>
                          <td>{s.count}</td>
                          <td>{money(s.sum)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <h4 className="mb-2">Причины отказа</h4>
              {amoData.data.byLossReason.length === 0 ? <div className="empty-state">Нет отказов за период</div> : (
                <div className="table-wrap mb-2">
                  <table>
                    <thead><tr><th>Причина</th><th>Кол-во</th></tr></thead>
                    <tbody>
                      {amoData.data.byLossReason.map(r => (
                        <tr key={r.reason}>
                          <td>{r.reason}</td>
                          <td>{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <h4 className="mb-2">По источникам</h4>
          {amoData.data.bySource.length === 0 ? <div className="empty-state">Нет данных за период</div> : (
            <div>
              {amoData.data.bySource.map(s => (
                <Bar key={s.source} label={`${s.source} (${s.count})`} value={Number(s.sum)} max={Math.max(1, ...amoData.data.bySource.map(x => Number(x.sum)))} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
